<template>
  <div class="rest-api-docs">
    <!-- Page Title Area -->
    <div class="page-title-area">
      <div class="container">
        <h2>REST API Documentation</h2>
      </div>
    </div>

    <!-- Breadcrumb Navigation - Moved to top and made horizontal -->
    <div v-if="apiData.Breadcrumbs && apiData.Breadcrumbs.length" class="breadcrumb-area">
      <div class="container">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/rest">
                <i class="fa fa-home"></i> API Root
              </router-link>
            </li>
            <li class="breadcrumb-item" v-for="(crumb, index) in apiData.Breadcrumbs" :key="index">
              <router-link :to="`/rest/${crumb.Key}`">
                {{ crumb.Display }}
              </router-link>
            </li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- Documentation Content Area -->
    <section class="ptb-80">
      <div class="container">
        <div class="row">
          <!-- Sidebar with API endpoints list -->
          <div class="col-lg-3">
            <div class="sidebar">
              <h3 class="mb-4">API Endpoints</h3>
              
              <!-- Search box for API endpoints -->
              <div class="mb-4">
                <input 
                  type="text" 
                  class="form-control" 
                  placeholder="Filter endpoints..."
                  v-model="searchQuery"
                >
              </div>
              
              <!-- Sub-endpoints of current API if available -->
              <div v-if="currentPath && apiData.prefix && apiData.prefix.length" class="sub-endpoints mb-4">
                <h5>Sub-endpoints</h5>
                <ul class="list-unstyled pl-2">
                  <li v-for="endpoint in apiData.prefix" :key="endpoint.name">
                    <router-link 
                      :to="`/rest/${currentPath}/${endpoint.name}`"
                      class="endpoint-link"
                    >
                      {{ endpoint.name }}
                      <span v-if="endpoint.methods && filterMethods(endpoint.methods).length" class="endpoint-methods">
                        <span 
                          v-for="method in filterMethods(endpoint.methods)" 
                          :key="method" 
                          class="method-badge method-small method-initial"
                          :class="`method-${method.toLowerCase()}`"
                          :title="method"
                        >
                          {{ method.charAt(0) }}
                        </span>
                      </span>
                    </router-link>
                  </li>
                </ul>
                <hr class="my-3">
              </div>
              
              <!-- Main API endpoints with all categories always expanded - only show on root -->
              <div v-if="!currentPath">
                <h5>All API Endpoints</h5>
                <ul class="list-unstyled">
                  <li v-for="category in filteredCategories" :key="category.name" class="mb-2">
                    <div class="category-name">
                      <i class="fa fa-folder-open"></i>
                      {{ category.name }}
                    </div>
                    <ul class="list-unstyled pl-3">
                      <li v-for="endpoint in category.endpoints" :key="endpoint">
                        <router-link 
                          :to="`/rest/${endpoint}`"
                          class="endpoint-link"
                          :class="{ active: currentPath === endpoint }"
                        >
                          {{ endpoint }}
                          
                          <!-- Show methods if available for this endpoint - only first letter -->
                          <span v-if="getEndpointMethods(endpoint)" class="endpoint-methods">
                            <span 
                              v-for="method in getEndpointMethods(endpoint)" 
                              :key="method" 
                              class="method-badge method-small method-initial"
                              :class="`method-${method.toLowerCase()}`"
                              :title="method"
                            >
                              {{ method.charAt(0) }}
                            </span>
                          </span>
                        </router-link>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Main content area -->
          <div class="col-lg-9">
            <div v-if="loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-2">Loading API documentation...</p>
            </div>
            <div v-else-if="error" class="alert alert-danger" role="alert">
              <strong>Error:</strong> {{ error }}
            </div>
            <div v-else>
              <!-- API Information -->
              <div v-if="!currentPath" class="rest-api-welcome">
                <h3>Welcome to the REST API Documentation</h3>
                <p>Select an API endpoint from the sidebar to view its documentation.</p>
                <div class="alert alert-info">
                  <strong>Tip:</strong> This interactive API explorer helps you understand the available API endpoints, their methods, parameters, and expected responses.
                </div>
                
                <h4 class="mt-5">Getting Started</h4>
                <p>The API follows RESTful principles where resources are accessed through URL paths. Each resource supports standard HTTP methods for different operations.</p>
                
                <h5>Base URL</h5>
                <div class="api-url">/_rest/[ResourceType]/[ResourceID]</div>
                
                <h5>Examples</h5>
                <ul>
                  <li><code>/_rest/User</code> - Access the User collection</li>
                  <li><code>/_rest/User/usr-abcdef123</code> - Access a specific user</li>
                </ul>
                
                <h5>Authentication</h5>
                <p>Most API endpoints require authentication. Several authentication methods are supported:</p>
                <ul>
                  <li>Bearer Token Authentication: <code>Authorization: Bearer &lt;token&gt;</code></li>
                  <li>API Key Authentication: <code>?_key=&lt;apikey&gt;</code></li>
                  <li>CSRF Token Authentication: <code>_csrf_token=&lt;token&gt;</code></li>
                </ul>
              </div>
              
              <!-- Specific API Documentation -->
              <div v-else class="api-details">
                <div class="api-header">
                  <h3>
                    <span class="api-path">{{ currentPath }}</span>
                    <span v-if="apiData.allowed_methods" class="api-methods">
                      <span 
                        v-for="method in apiData.allowed_methods" 
                        :key="method" 
                        class="method-badge"
                        :class="`method-${method.toLowerCase()}`"
                      >
                        {{ method }}
                      </span>
                    </span>
                  </h3>
                  <div v-if="apiData.description" class="api-description mt-3" v-html="formatDescription(apiData.description)">
                  </div>
                </div>
                
                <!-- Resource Details -->
                <div v-if="apiData.table" class="api-resource mt-4">
                  <h4>Resource Details</h4>
                  <div class="resource-info">
                    <div class="resource-row">
                      <div class="resource-label">Name:</div>
                      <div class="resource-value">{{ apiData.table.Name }}</div>
                    </div>
                    <div class="resource-row" v-if="apiData.table.Struct">
                      <div class="resource-label">Fields:</div>
                      <div class="resource-value">{{ Object.keys(apiData.table.Struct).length - (apiData.table.Struct._default_sort ? 1 : 0) - (apiData.table.Struct._key_prefix ? 1 : 0) - (apiData.table.Struct._primary ? 1 : 0) }} fields</div>
                    </div>
                    <div class="resource-row" v-if="apiData.table.Struct && apiData.table.Struct._primary">
                      <div class="resource-label">Primary Key:</div>
                      <div class="resource-value">{{ Array.isArray(apiData.table.Struct._primary) ? apiData.table.Struct._primary.join(', ') : apiData.table.Struct._primary }}</div>
                    </div>
                  </div>
                  
                  <!-- Fields -->
                  <div v-if="apiData.table.Struct" class="fields-section mt-4">
                    <h5>Fields</h5>
                    <div class="api-fields">
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Type</th>
                            <th>Required</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(field, fieldName) in filteredFields" :key="fieldName">
                            <td><code>{{ fieldName }}</code></td>
                            <td>{{ getFieldType(field) }}</td>
                            <td>{{ field.null === false ? 'Yes' : 'No' }}</td>
                            <td v-html="formatDescription(field.description || '')"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <!-- Available Methods -->
                <div v-if="apiData.func && apiData.func.length" class="api-methods-section mt-4">
                  <h4>Available Methods</h4>
                  
                  <!-- Function List -->
                  <div class="methods-list">
                    <div v-for="func in apiData.func" :key="func.name" class="method-item mt-4 p-3">
                      <div class="method-header">
                        <h5>{{ func.name }}</h5>
                        <div class="method-type">{{ func.static ? 'Static Method' : 'Instance Method' }}</div>
                      </div>
                      
                      <div v-if="func.description" class="method-description mt-2">
                        <div v-html="formatDescription(func.description)"></div>
                      </div>
                      
                      <!-- Arguments -->
                      <div v-if="func.args && func.args.length" class="method-arguments mt-3">
                        <h6>Arguments</h6>
                        <table class="table table-striped">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Type</th>
                              <th>Required</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="arg in func.args" :key="arg.name">
                              <td><code>{{ arg.name }}</code></td>
                              <td>{{ arg.type || 'string' }}</td>
                              <td>{{ arg.required ? 'Yes' : 'No' }}</td>
                              <td v-html="formatDescription(arg.description || '')"></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      
                      <div v-if="func.return_description" class="method-return mt-3">
                        <h6>Returns</h6>
                        <div v-html="formatDescription(func.return_description)"></div>
                      </div>
                      
                      <!-- Usage example -->
                      <div class="method-usage mt-3">
                        <h6>Usage Example</h6>
                        <pre class="code-example">
// JavaScript
klbfw.rest('{{ currentPath }}{{ (func.static ? ':' : '/\${id}:') + func.name }}', '{{ func.name === 'process' ? 'POST' : apiData.allowed_methods ? apiData.allowed_methods[0] : 'POST' }}', {
  {{func.args && func.args.length ? func.args.map(arg => `${arg.name}: ${getExampleValue(arg)}`) .join(',\n  ') : ''}}
})</pre>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Procedure if present -->
                <div v-if="apiData.procedure" class="api-methods-section mt-4">
                  <h4>Procedure</h4>
                  
                  <div class="procedure-details">
                    <div class="procedure-header">
                      <h5>{{ apiData.procedure.name }}</h5>
                      <div class="procedure-type">{{ apiData.procedure.static ? 'Static Method' : 'Instance Method' }}</div>
                    </div>
                    
                    <div v-if="apiData.procedure.description" class="procedure-description mt-2">
                      <div v-html="formatDescription(apiData.procedure.description)"></div>
                    </div>
                    
                    <!-- Arguments -->
                    <div v-if="apiData.procedure.args && apiData.procedure.args.length" class="procedure-arguments mt-3">
                      <h6>Arguments</h6>
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Required</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="arg in apiData.procedure.args" :key="arg.name">
                            <td><code>{{ arg.name }}</code></td>
                            <td>{{ arg.type || 'string' }}</td>
                            <td>{{ arg.required ? 'Yes' : 'No' }}</td>
                            <td v-html="formatDescription(arg.description || '')"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <div v-if="apiData.procedure.return_description" class="procedure-return mt-3">
                      <h6>Returns</h6>
                      <div v-html="formatDescription(apiData.procedure.return_description)"></div>
                    </div>
                    
                    <!-- Usage example -->
                    <div class="procedure-usage mt-3">
                      <h6>Usage Example</h6>
                      <pre class="code-example">
// JavaScript
klbfw.rest('{{ currentPath }}', '{{ apiData.allowed_methods ? apiData.allowed_methods[0] : 'POST' }}', {
  {{apiData.procedure.args && apiData.procedure.args.length ? apiData.procedure.args.map(arg => `${arg.name}: ${getExampleValue(arg)}`) .join(',\n  ') : ''}}
})</pre>
                    </div>
                  </div>
                </div>
                
                <!-- Sub-endpoints section removed and moved to sidebar -->
                
                <!-- Raw Data Toggle -->
                <div class="raw-data-toggle mt-5">
                  <button class="btn btn-secondary" @click="showRawData = !showRawData">
                    {{ showRawData ? 'Hide Raw Response' : 'Show Raw Response' }}
                  </button>
                  
                  <div v-if="showRawData" class="raw-data mt-3">
                    <pre class="raw-json">{{ JSON.stringify(apiData, null, 2) }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { rest } from '@karpeleslab/klbfw'
import { marked } from 'marked'

// Configure marked for secure rendering
marked.setOptions({
  breaks: true, // Convert line breaks to <br>
  gfm: true, // Enable GitHub Flavored Markdown
  headerIds: false, // Disable header IDs for security
  mangle: false // Don't mangle email addresses
})

export default {
  name: 'RestApiDocs',
  props: {
    path: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const apiData = ref({})
    const apiList = ref([])
    const loading = ref(false)
    const error = ref(null)
    const expandedCategories = ref([])
    const searchQuery = ref('')
    const showRawData = ref(false)
    
    // Current path - convert array to string if needed
    const currentPath = computed(() => {
      if (Array.isArray(props.path)) {
        return props.path.join('/')
      }
      return props.path || ''
    })
    
    // Organize API endpoints into categories
    const categories = computed(() => {
      if (!apiList.value || !apiList.value.length) return []
      
      // When apiList contains objects in the format { name: "Category", methods: [...] }
      // Organize them by first letter for better navigation
      const result = []
      const categoriesMap = {}
      
      apiList.value.forEach(endpoint => {
        // Get first letter as category
        const firstLetter = endpoint.name ? endpoint.name.charAt(0).toUpperCase() : '#'
        
        if (!categoriesMap[firstLetter]) {
          categoriesMap[firstLetter] = {
            name: firstLetter,
            endpoints: []
          }
          result.push(categoriesMap[firstLetter])
        }
        
        categoriesMap[firstLetter].endpoints.push(endpoint.name)
      })
      
      // Sort categories and endpoints
      result.forEach(cat => {
        cat.endpoints.sort()
      })
      result.sort((a, b) => a.name.localeCompare(b.name))
      
      return result
    })
    
    // Filter categories and endpoints based on search query
    const filteredCategories = computed(() => {
      if (!searchQuery.value) return categories.value
      
      const query = searchQuery.value.toLowerCase()
      
      return categories.value.map(category => {
        // Filter endpoints within each category
        const matchingEndpoints = category.endpoints.filter(endpoint => 
          endpoint.toLowerCase().includes(query)
        )
        
        if (matchingEndpoints.length > 0) {
          return {
            name: category.name,
            endpoints: matchingEndpoints
          }
        }
        
        return null
      }).filter(Boolean) // Remove empty categories
    })
    
    // Filter out special fields from the struct
    const filteredFields = computed(() => {
      if (!apiData.value.table || !apiData.value.table.Struct) return {}
      
      const result = {}
      Object.entries(apiData.value.table.Struct).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          result[key] = value
        }
      })
      
      return result
    })
    
    // Helper function to get field type display
    const getFieldType = (field) => {
      if (!field) return ''
      
      let type = field.type || ''
      
      if (field.size) {
        type += `[${field.size}]`
      }
      
      if (field.values && Array.isArray(field.values)) {
        type += ` (${field.values.join(', ')})`
      }
      
      return type
    }
    
    // Helper function to format description text with proper markdown support
    const formatDescription = (text) => {
      try {
        if (text === null || text === undefined) return ''
        
        // Make sure we're working with a string
        const textStr = String(text)
        
        // For empty or very short text, just return a simple paragraph
        if (textStr.length === 0) return ''
        if (textStr.length < 3 && !textStr.includes('\n')) return `<p>${textStr}</p>`
        
        // Use marked to properly parse markdown
        const html = marked.parse(textStr)
        
        return html
      } catch (error) {
        // Fallback: if any error occurs, return the original content safely escaped
        console.error('Error formatting description:', error)
        
        // Escape HTML to prevent XSS
        const escaped = String(text || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
        
        return `<p>${escaped}</p>`
      }
    }
    
    // Helper function to generate example values for arguments
    const getExampleValue = (arg) => {
      if (!arg) return '""'
      
      if (arg.type === 'array') {
        return '[]'
      } else if (arg.type === 'object') {
        return '{}'
      } else if (arg.type === 'number' || arg.type === 'integer') {
        return arg.example || '0'
      } else if (arg.type === 'boolean') {
        return arg.example || 'false'
      } else if (arg.name.includes('__')) {
        // For UUIDs, use a sample format
        return '"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"'
      } else {
        return `"${arg.example || '...'}"`
      }
    }
    
    // Helper function to filter methods (hide OPTIONS and SUB)
    const filterMethods = (methods) => {
      if (!methods || !Array.isArray(methods)) return []
      return methods.filter(method => method !== 'SUB' && method !== 'OPTIONS')
    }
    
    // Helper function to get methods for an endpoint in the sidebar
    const getEndpointMethods = (endpointName) => {
      if (!apiList.value || !apiList.value.length) return null
      
      // Find the endpoint in the apiList
      const endpoint = apiList.value.find(item => item.name === endpointName)
      if (!endpoint || !endpoint.methods) return null
      
      // Return only the important methods for display
      return filterMethods(endpoint.methods)
    }
    
    // Fetch API documentation using OPTIONS method
    const fetchApiDoc = async (path) => {
      loading.value = true
      error.value = null
      
      try {
        if (!path) {
          // Fetch the root API list
          const response = await rest('', 'OPTIONS')
          
          if (response && response.data && response.data.prefix) {
            apiList.value = response.data.prefix
            apiData.value = response.data
            
            // Categories are now always expanded by default
          } else {
            error.value = 'Failed to load API endpoints'
          }
        } else {
          // Fetch specific API documentation
          // Since path is an array due to the ':path*' route parameter,
          // we need to join it with '/' to create the proper path string
          const formattedPath = Array.isArray(path) ? path.join('/') : path
          
          const response = await rest(formattedPath, 'OPTIONS')
          
          if (response && response.data) {
            apiData.value = response.data
          } else {
            error.value = 'Failed to load API documentation'
          }
        }
        
        // Set page title - use formatted path if available
        document.title = path 
          ? `${Array.isArray(path) ? path.join('/') : path} API Documentation - AtOnline.com`
          : 'REST API Documentation - AtOnline.com'
          
      } catch (err) {
        console.error('Error fetching API documentation:', err)
        error.value = err.message || 'Failed to load API documentation'
      } finally {
        loading.value = false
      }
    }
    
    // Fetch initial data
    onMounted(() => {
      fetchApiDoc(currentPath.value)
    })
    
    // Watch for path changes
    watch(() => props.path, (newPath) => {
      showRawData.value = false // Reset raw data view on path change
      fetchApiDoc(newPath)
    })
    
    return {
      apiData,
      apiList,
      loading,
      error,
      currentPath,
      categories,
      filteredCategories,
      expandedCategories,
      searchQuery,
      filteredFields,
      showRawData,
      getFieldType,
      formatDescription,
      getExampleValue,
      getEndpointMethods,
      filterMethods
    }
  }
}
</script>

<style scoped>
.rest-api-docs {
  min-height: 100vh;
}

.sidebar {
  background: #f7fafd;
  padding: 20px;
  border-radius: 5px;
  position: sticky;
  top: 80px;
}

.breadcrumb-area {
  background-color: #f8f9fa;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
}

.breadcrumb {
  background: transparent;
  margin-bottom: 0;
  padding: 8px 0;
}

.breadcrumb-item a {
  color: #44ce6f;
  font-weight: 500;
  text-decoration: none;
}

.breadcrumb-item a:hover {
  text-decoration: underline;
}

.breadcrumb-item + .breadcrumb-item::before {
  content: "›";
  color: #6c757d;
  font-weight: bold;
}

.category-name {
  font-weight: bold;
  padding: 5px 0;
  border-radius: 3px;
  color: #44ce6f;
  margin-bottom: 5px;
}

.endpoint-link {
  display: block;
  padding: 5px 0;
  color: #333;
  text-decoration: none;
  font-size: 0.95rem;
  border-radius: 3px;
  padding-left: 5px;
}

.endpoint-link:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #44ce6f;
}

.endpoint-link.active {
  color: #44ce6f;
  font-weight: bold;
  background-color: rgba(68, 206, 111, 0.1);
}

.endpoint-methods {
  display: block;
  margin-top: 4px;
  margin-left: 5px;
}

.endpoint-link .method-small {
  font-size: 0.6rem;
  padding: 1px 3px;
  margin-right: 2px;
  opacity: 0.85;
}

.api-header {
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.api-path {
  font-family: monospace;
  background: #f5f5f5;
  padding: 3px 7px;
  border-radius: 3px;
}

.api-description {
  line-height: 1.6;
}

.api-description p {
  margin-bottom: 0.75rem;
}

.api-description ul, .api-description ol {
  padding-left: 20px;
  margin-bottom: 0.75rem;
}

.api-description li {
  margin-bottom: 5px;
}

.api-description pre {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 15px;
  border-radius: 4px;
  margin: 10px 0;
  font-family: monospace;
  white-space: pre-wrap;
  font-size: 0.9rem;
  overflow-x: auto;
}

.api-description code {
  background: #f1f1f1;
  padding: 2px 4px;
  border-radius: 3px;
  color: #e83e8c;
  font-size: 0.9em;
}

.api-description pre code {
  background: none;
  padding: 0;
  color: #f8f8f2;
}

.api-description blockquote {
  border-left: 4px solid #44ce6f;
  padding-left: 15px;
  margin: 15px 0;
  font-style: italic;
  color: #666;
}

.api-description h1, .api-description h2,
.api-description h3, .api-description h4,
.api-description h5, .api-description h6 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.api-description table {
  width: 100%;
  border-collapse: collapse;
  margin: 15px 0;
}

.api-description th, .api-description td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.api-description th {
  background-color: #f7f7f7;
  font-weight: bold;
}

.api-methods {
  margin-left: 10px;
}

.method-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: bold;
  margin-left: 5px;
  color: white;
}

.method-get {
  background-color: #61affe;
}

.method-post {
  background-color: #49cc90;
}

.method-put, .method-patch {
  background-color: #fca130;
}

.method-delete {
  background-color: #f93e3e;
}

.method-options {
  background-color: #0d5aa7;
}

.method-sub {
  background-color: #4f0785;
}

.method-small {
  font-size: 0.65rem;
  padding: 1px 5px;
  margin-left: 3px;
}

.method-initial {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-weight: bold;
  font-size: 0.6rem;
}

.sub-methods {
  margin-left: 5px;
}

.resource-info {
  background: #f7f7f7;
  padding: 15px;
  border-radius: 5px;
}

.resource-row {
  display: flex;
  margin-bottom: 10px;
}

.resource-row:last-child {
  margin-bottom: 0;
}

.resource-label {
  font-weight: bold;
  width: 120px;
}

.api-fields {
  margin-top: 15px;
}

code {
  background: #f1f1f1;
  padding: 2px 4px;
  border-radius: 3px;
  color: #e83e8c;
}

/* Markdown styles within table cells */
td p {
  margin: 0;
  margin-bottom: 0.5rem;
}

td p:last-child {
  margin-bottom: 0;
}

td code {
  background: #f1f1f1;
  padding: 1px 3px;
  border-radius: 2px;
  color: #e83e8c;
  font-size: 0.85em;
}

td ul, td ol {
  margin: 0;
  padding-left: 20px;
  margin-bottom: 0.5rem;
}

td ul:last-child, td ol:last-child {
  margin-bottom: 0;
}

td li {
  margin-bottom: 2px;
}

td pre {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 8px;
  border-radius: 3px;
  margin: 5px 0;
  font-size: 0.85rem;
  overflow-x: auto;
}

td pre code {
  background: none;
  padding: 0;
  color: #f8f8f2;
}

.code-example {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 15px;
  border-radius: 5px;
  overflow-x: auto;
}

.raw-json {
  background: #f0f0f0;
  padding: 15px;
  border-radius: 5px;
  overflow-x: auto;
  font-family: monospace;
  font-size: 0.85rem;
}

.procedure-header, .method-header {
  display: flex;
  align-items: center;
}

.procedure-type, .method-type {
  margin-left: 10px;
  font-size: 0.85rem;
  color: #666;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 10px;
}

.args-list li {
  margin-bottom: 5px;
}

.api-url {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 10px 15px;
  border-radius: 5px;
  font-family: monospace;
  margin: 10px 0;
}

.sub-endpoints-list li {
  margin-bottom: 15px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 5px;
}

.method-item {
  background-color: #f8f9fa;
  border-radius: 5px;
  border-left: 4px solid #44ce6f;
}

.method-description p, .procedure-description p {
  margin-bottom: 0.75rem;
}

.method-description ul, .procedure-description ul {
  padding-left: 20px;
  margin-bottom: 0.75rem;
}

.method-description li, .procedure-description li {
  margin-bottom: 5px;
}

.method-description pre, .procedure-description pre {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 15px;
  border-radius: 4px;
  margin: 10px 0;
  font-family: monospace;
  white-space: pre-wrap;
  font-size: 0.9rem;
  overflow-x: auto;
}

.method-description code, .procedure-description code {
  background: #f1f1f1;
  padding: 2px 4px;
  border-radius: 3px;
  color: #e83e8c;
  font-size: 0.9em;
}

.method-description pre code, .procedure-description pre code {
  background: none;
  padding: 0;
  color: #f8f8f2;
}

.method-description blockquote, .procedure-description blockquote {
  border-left: 4px solid #44ce6f;
  padding-left: 15px;
  margin: 15px 0;
  font-style: italic;
  color: #666;
}

.method-description h1, .procedure-description h1,
.method-description h2, .procedure-description h2,
.method-description h3, .procedure-description h3,
.method-description h4, .procedure-description h4,
.method-description h5, .procedure-description h5,
.method-description h6, .procedure-description h6 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.method-description table, .procedure-description table {
  width: 100%;
  border-collapse: collapse;
  margin: 15px 0;
}

.method-description th, .procedure-description th,
.method-description td, .procedure-description td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.method-description th, .procedure-description th {
  background-color: #f7f7f7;
  font-weight: bold;
}

.code-block {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 4px;
  margin: 10px 0;
  font-family: monospace;
  white-space: pre-wrap;
  font-size: 0.9rem;
}

/* Removed old breadcrumb styles in favor of Bootstrap breadcrumbs */
</style>
